import React, { useState, useEffect, useMemo } from 'react';
import _, { clone } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher, PRODUCT_CODE } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  StyledInputList,
  StyledSettingBox,
  StyledSettingList,
  StyledSettingWrapper,
  StyledReportWrap,
  StyledSwButtonSection,
} from './style';
import { submitDebounce } from 'utils/lib';

interface sendToPacsState {
  [key: string]: boolean;
}

const NIPDSendToPacs = () => {
  const { submitConfirmDialog, confirmTurnAutomaticallyOnOffDialog } = useStaticDialog();
  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  // const [data, setData] = useState<sendToPacsState>({
  //   sendResult: false,
  //   saveReport: false,
  //   saveResliceImage: false,
  //   saveSmwiImage: false,
  // });
  const [data, setData] = useState<sendToPacsState>({
    sendResult: false,
    saveAnalysisReport: false,
    saveSegmentationReport: false,
    saveResliceImage: false,
    saveSmwiImage: false,
  });
  // const [backupData, setBackupData] = useState<sendToPacsState>({
  //   sendResult: false,
  //   saveReport: false,
  //   saveResliceImage: false,
  //   saveSmwiImage: false,
  // });
  const [backupData, setBackupData] = useState<sendToPacsState>({
    sendResult: false,
    saveAnalysisReport: false,
    saveSegmentationReport: false,
    saveResliceImage: false,
    saveSmwiImage: false,
  });

  /**
   * 제품별 각각 다 들어갈 수 있는 모듈로 제품에 따라
   * api 경로 다르게 반환 하기 위한 함수
   */
  const getAPIUrl = () => {
    let productType = '';

    switch (PRODUCT_CODE) {
      case 'pd':
        productType = 'ipd'; // 백엔드 url에 맞춰서 바꾸기
        break;
      case 'mn1q':
        productType = 'ni';
        break;
      case 'mpdia':
        productType = 'ipd';
        break;
    }
    return `api/setting/${productType}/pacs`;
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */

  const setDataValue = (key: string, value: boolean) => {
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });
  };

  /**
   * 스위치 변경 컨펌
   * @param key
   * @param value
   */
  const confirmTurnOnOff = (key: string, value: boolean) => {
    const turn = value ? 'on' : 'off';

    //먼저 바꾸고 컨펌에 따라 결과 확정
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });

    confirmTurnAutomaticallyOnOffDialog(
      turn,
      () => null,
      () => {
        setDataValue(key, !value);
      },
    );
  };

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    // valid 체크
    const url = getAPIUrl();
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _tmpPostData = {
      sendResult: data.sendResult,
      saveAnalysisReport: data.saveAnalysisReport,
      saveSegmentationReport: data.saveSegmentationReport,
      saveResliceImage: data.saveResliceImage,
      saveSmwiImage: data.saveSmwiImage,
    };

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: {
        ..._tmpPostData,
      },
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData(_tmpPostData);
          setBackupData(data);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * get send to pacs data
   */
  const getData = async () => {
    const url = getAPIUrl(); // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          const _tmpGetData = {
            sendResult: r.result.sendResult,
            // saveReport: r.result.saveReport,
            saveAnalysisReport: r.result.saveAnalysisReport,
            saveSegmentationReport: r.result.saveSegmentationReport,
            saveResliceImage: r.result.saveResliceImage,
            saveSmwiImage: r.result.saveSmwiImage,
          };
          setData(_tmpGetData);
          setBackupData(_tmpGetData);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, []);

  return (
    <>
      <label>Send to PACS</label>
      <StyledReportWrap>
        <StyledSettingWrapper>
          <strong>Send results automatically</strong>
          <StyledSettingBox>
            <StyledSettingList>
              <li>
                <StyledInputList>
                  <span className={'send-off'}>Off</span>
                  <Switch defaultValue={data.sendResult} onChangeValue={(e) => confirmTurnOnOff('sendResult', e)} />
                  <span>On</span>
                </StyledInputList>
              </li>
            </StyledSettingList>
          </StyledSettingBox>
        </StyledSettingWrapper>
        <StyledSettingWrapper>
          <strong>Save results setting</strong>
          <StyledSettingBox>
            <StyledSettingList>
              <li>
                <StyledInputList>
                  <li className={'sub-title'}>Report Type</li>
                </StyledInputList>
                <StyledInputList>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'saveAnalysisReport'}
                      checked={data.saveAnalysisReport}
                      onChange={(e) => setDataValue('saveAnalysisReport', e.target.checked)}
                    >
                      Analysis Report
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'saveSegmentationReport'}
                      checked={data.saveSegmentationReport}
                      onChange={(e) => setDataValue('saveSegmentationReport', e.target.checked)}
                    >
                      Segmentation Report
                    </Input>
                  </li>
                </StyledInputList>
              </li>
              <li>
                <StyledInputList>
                  <StyledInputList>
                    <li className={'sub-title'}>Image Type</li>
                  </StyledInputList>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'saveResliceImage'}
                      checked={data.saveResliceImage}
                      onChange={(e) => setDataValue('saveResliceImage', e.target.checked)}
                    >
                      Analysis(Reslice, Segmentation)
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'saveSmwiImage'}
                      checked={data.saveSmwiImage}
                      onChange={(e) => setDataValue('saveSmwiImage', e.target.checked)}
                    >
                      SMWI
                    </Input>
                  </li>
                </StyledInputList>
              </li>
              <li className="annotation">
                *Unchecked items are not saved on the server and Resend to PACS is not available.
              </li>
            </StyledSettingList>
          </StyledSettingBox>
        </StyledSettingWrapper>
      </StyledReportWrap>
      <StyledSwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={_.isEqual(data, backupData)}
        >
          Submit
        </PrimaryButton>
      </StyledSwButtonSection>
    </>
  );
};

export default NIPDSendToPacs;
